import type { ComponentPropsWithRef, ReactNode } from "react";
import { Panel, type PanelProps } from "./Panel";
import { cn } from "@/lib/utils";

/** The elements an empty state is drawn as. An empty row of a list is an `li`. */
type EmptyTag = "div" | "li" | "p";

interface Empty<T extends EmptyTag> {
  as?: T;
  shape?: PanelProps["shape"];
  children?: ReactNode;
}

export type EmptyStateProps<T extends EmptyTag = "div"> = Empty<T> &
  Omit<ComponentPropsWithRef<T>, keyof Empty<T> | "as">;

/**
 * A surface with nothing on it yet, said in the same words everywhere.
 *
 * Dashed and transparent rather than a card: a card with nothing in it reads as
 * a thing, and four of these were cards until the surface slice gave each one
 * `bg-transparent` separately. The dash says the shape is waiting to be filled
 * rather than empty on purpose.
 */
export function EmptyState<T extends EmptyTag = "div">({
  as,
  shape = "card",
  className,
  children,
  ...rest
}: EmptyStateProps<T>) {
  // Widened once, here, the way `Panel` widens its own tag: TypeScript cannot
  // reconcile a generic wrapper's props with a generic component's without
  // being shown they are the same `T`, and no single ref type satisfies `div`
  // and `li` at once. The caller is still checked against the tag it named,
  // which is the boundary that matters; what is given up is the handoff
  // between two components that agree by construction.
  const shell = Panel as (props: PanelProps) => ReactNode;
  return shell({
    as: (as ?? "div") as "div",
    shape,
    pad: "none",
    className: cn(
      "flex items-center justify-center gap-2 border-dashed bg-transparent px-3 py-6 text-center text-sm text-muted-foreground",
      className,
    ),
    children,
    ...(rest as ComponentPropsWithRef<"div">),
  });
}
